<template>
  <div>
    <base-button block type="primary" class="mt-0" @click="modal = true">
      {{ $t('system_account.create.btnLabel') }}
    </base-button>

    <modal id="createSystemAccount"
           :show.sync="modal"
           modal-classes="modal-dialog-centered modal-lg"
           showClose @close="resetForm()">
      <template slot="header">
        <h4 class="modal-title">
          {{ $t('system_account.create.titleText') }}
        </h4>
      </template>
      <template>
        <div class="text-center text-muted mb-2">
          <small>{{ $t('system_account.create.helpText') }}</small>
        </div>

        <base-alert v-if="alert" dismissible :dismiss-callback="() => alert=false" type="danger">
          <!-- TODO: add error translation -->
          {{ getActionErrorDetails }}
        </base-alert>

        <form role="form" v-on:submit.prevent="create()">
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.username') }}</label>
            <div class="col">
              <base-input
                name="username"
                v-validate="modelValidations.required"
                :error="getError('username')"
                v-model="system_account.username"
              >
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.first_name') }}</label>
            <div class="col">
              <base-input
                name="first_name"
                v-validate="modelValidations.required"
                :error="getError('first_name')"
                v-model="system_account.first_name"
              >
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.last_name') }}</label>
            <div class="col">
              <base-input
                name="last_name"
                v-validate="modelValidations.required"
                :error="getError('last_name')"
                v-model="system_account.last_name"
              >
              </base-input>
            </div>
          </div>

          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.email') }}</label>
            <div class="col">
              <base-input
                type="email"
                name="email"
                v-validate="modelValidations.email"
                :error="getError('email')"
                v-model="system_account.email"
              >
              </base-input>
            </div>
          </div>

          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.contact_number') }}</label>
            <div class="col">
              <base-input
                type="string"
                v-model="system_account.contact_number"
              >
              </base-input>
            </div>
          </div>

          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.organisation') }}</label>
            <div class="col">
              <base-input
                type="string"
                v-model="system_account.organisation"
              >
              </base-input>
            </div>
          </div>

          <!-- language select -->
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.language') }}</label>
            <div class="col form-group">
              <el-select
                class="select-primary"
                name="role"
                v-model="system_account.language"
              >
                <el-option
                  v-for="option in availableLanguages"
                  class="select-primary"
                  :value="option"
                  :label="option"
                  :key="option"
                >
                </el-option>
              </el-select>
            </div>
          </div>

          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.cidrs') }}</label>
            <div class="col">
              <div class="form-group">
                <tags-input v-model="system_account.cidrs"
                            :input-placeholder="$t('system_account.form.cidrPlaceholder')"
                            input-size="" tag-size="medium"></tags-input>
              </div>
            </div>
          </div>

          <!-- role select -->
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('system_account.details.role') }}</label>
            <div class="col form-group">
              <el-select
                class="select-primary"
                name="role"
                :placeholder="$t('system_account.form.rolePlaceholder')"
                v-model="system_account.role"
              >
                <el-option
                  v-for="option in availableRoles"
                  class="select-primary"
                  :value="option.value"
                  :label="$t(`roleNames.${option.value}`)"
                  :key="option.label"
                >
                </el-option>
              </el-select>
            </div>
          </div>

          <div class="text-center">
            <base-button
              :loading="loading"
              type="primary"
              class="my-4"
              @click="validate()"
            >{{ $t('system_account.create.actionBtnLabel') }}
            </base-button>
          </div>
        </form>
      </template>
    </modal>
  </div>
</template>


<script>
import {BaseAlert, Modal, TagsInput} from 'src/components';
import {Option, Select} from 'element-ui';
import {mapActions, mapState} from "vuex";
import languages from './languages';
import system_account from "@/store/modules/system_account";

export default {
  name: 'create-system-accounts',
  components: {
    [Option.name]: Option,
    [Select.name]: Select,
    Modal,
    TagsInput,
    BaseAlert
  },
  data() {
    return {
      modal: false,
      alert: false,
      system_account: {
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        contact_number: '',
        organisation: '',
        role: '',
        cidrs: ["0.0.0.0/0", "0::0/0"],
        language: 'lt'
      },
      modelValidations: {
        required: {
          required: true
        },
        email: {
          required: true,
          email: true
        }
      },
      availableLanguages: languages
    }
  },
  computed: {
    ...mapState({
      loading: state => state.system_account.updating,
      error: state => state.system_account.error,
      created: state => state.system_account.single,
      roles: state => state.role.roles,
    }),

    availableRoles() {
      let existingRoles = []
      for (let i = 0; i < this.roles.length; i++) {
        if (!this.roles[i].name.startsWith('bot:')) {
          // IGNORE BOT ROLES
          existingRoles.push({
            value: this.roles[i].name,
            label: this.roles[i].name,
          })
        }
      }
      // Add `admin` role if connected user is `admin`
      if (this.$auth.check('admin')) {
        existingRoles.push({
          value: 'admin',
          label: 'admin',
        })
      }
      return existingRoles
    },

    getActionErrorDetails() {
      return this.error
    }
  },
  methods: {
    ...mapActions(['CreateSystemAccount']),
    getError(fieldName) {
      return this.errors.first(fieldName);
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        if (isValid) {
          this.create()
        }
      });
    },
    handleRoleChange: function (role) {
      this.system_account.role = role
    },
    create: function () {
      this.alert = false;
      this.CreateSystemAccount(this.system_account).then(() => {
        if (this.error != null) {
          this.alert = true
          // got error, don't reset form
        } else {
          this.$router.push({
            name: 'system_account',
            params: {id: this.created.id}
          }).catch(err => {
          })
          // closing dialog
          this.resetForm();
        }
      });
    },
    resetForm: function () {
      this.modal = false;
      this.alert = false;
      // resetting form
      this.system_account = {
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        role: '',
        contact_number: '',
        organisation: '',
        cidrs: ["0.0.0.0/0", "0::0/0"],
        language: 'lt'
      }

      // reset validation errors
      this.$validator.reset()
    }
  },
}
</script>
